import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Container } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLoader } from "../../../context/LoaderContext";
import { useNavigate } from "react-router-dom";
import List from "./List";
import { fetchPlans, fetchFeatures } from "../../../redux/slice/subscriptionSlice";
import ManageFeatures from "./ManageFeatures";

const Plans = () => {
  const dispatch = useDispatch();
  const { setLoading } = useLoader();
  const navigate = useNavigate();
  const [limit, setLimit] = useState({ value: 10, label: 10 });

  const headerPortalTarget = document.getElementById("header-portal-target");

  const headerContent = (
    <div className="d-flex align-items-center justify-content-between w-100">
      <h4 className="fw-semibold mb-0 text-primary fs-lg">
        <span className="text-muted fw-normal">Subscription / </span>Plans
      </h4>
    </div>
  );

  const { plans = [], isLoading, total } = useSelector(
    (state) => state.subscription || {}
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedItem, setSelectedItem] = useState(null);
  const [openManageModal, setOpenManageModal] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchData = useCallback(() => {
    dispatch(
      fetchPlans({
        search: debouncedSearchTerm,
        page: currentPage,
        limit: limit?.value,
      })
    );
  }, [dispatch, debouncedSearchTerm, currentPage, limit?.value]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Also fetch all available features once for mapping
  useEffect(() => {
    dispatch(fetchFeatures({ limit: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    if (!debouncedSearchTerm && plans?.length === 0) {
      setLoading(isLoading);
    } else {
      setLoading(false);
    }
  }, [isLoading, setLoading, plans?.length, debouncedSearchTerm]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleEdit = (item) => {
    navigate(`/subscriptions/plans/edit/${item.id}`);
  };

  const handleAdd = () => {
    navigate("/subscriptions/plans/add");
  };

  const handleManage = (item) => {
    setSelectedItem(item);
    setOpenManageModal(true);
  };

  const handlePageChange = (newPageNumber) => {
    setCurrentPage(newPageNumber);
  };

  const paginationConfig = useMemo(() => {
    const pageSize = limit?.value || 10;
    const totalPages = Math.ceil((total || 0) / pageSize);
    return {
      total: total || 0,
      totalPages,
      currentPage,
      setCurrentPage,
      pageSize,
      setPageSize: (size) => {
        setLimit({ value: size, label: size });
        setCurrentPage(1);
      },
      onPageChange: handlePageChange,
    };
  }, [total, currentPage, limit]);

  return (
    <>
      {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}
      <Container fluid className="px-0">
        <div className="bg-white rounded-2 overflow-hidden common-shadow mt-4">
          <List
            data={plans}
            isLoading={isLoading}
            searchTerm={searchTerm}
            setSearchTerm={handleSearchChange}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            handleEdit={handleEdit}
            handleAdd={handleAdd}
            handleManage={handleManage}
            refresh={fetchData}
            limit={limit?.value}
            pagination={paginationConfig}
          />
        </div>

        <ManageFeatures
          isOpen={openManageModal}
          onClose={() => {
            setOpenManageModal(false);
            setSelectedItem(null);
          }}
          plan={selectedItem}
          title={selectedItem ? `Manage Features for ${selectedItem.name}` : ""}
          size="lg"
        />
      </Container>
    </>
  );
};

export default Plans;
